const REMOTE_IMG_ATTR = 'data-remote-img';

const readHtmlAttr = (attrs: string, name: string): string => {
    const match = String(attrs || '').match(
        new RegExp(`(?:^|\\s)${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i')
    );
    return (match?.[1] || match?.[2] || match?.[3] || '').trim();
};

const escapeHtmlAttr = (value: string): string => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const stripTags = (value: string): string => String(value || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

/**
 * True when HTML contains at least one intake remote-image placeholder.
 */
export function hasRemoteImagePlaceholders(html: string): boolean {
    return new RegExp(`<a\\b[^>]*\\b${REMOTE_IMG_ATTR}\\b`, 'i').test(html || '');
}

/**
 * True when any of the HTML fragments contains a remote-image placeholder.
 */
export function htmlSourcesHaveRemoteImages(sources: Array<string | null | undefined>): boolean {
    return sources.some((html) => hasRemoteImagePlaceholders(String(html || '')));
}

/**
 * Replace `data-remote-img` placeholder links with `<img>` tags.
 * Leaves other anchors untouched.
 * Matches placeholders by attribute first so nested tracking wrappers
 * (`<a href=track><a href=cdn data-remote-img>…</a></a>`) still hydrate.
 */
export function hydrateRemoteImages(html: string): string {
    if (!html || !hasRemoteImagePlaceholders(html))
        return html;

    return html.replace(
        new RegExp(`<a\\b([^>]*\\b${REMOTE_IMG_ATTR}\\b[^>]*)>([\\s\\S]*?)<\\/a>`, 'gi'),
        (match, rawAttrs = '', inner = '') => {
            const href = readHtmlAttr(rawAttrs, 'href');
            if (!/^https?:\/\//i.test(href))
                return match;

            const alt = stripTags(inner) || 'Image';
            return `<img src="${escapeHtmlAttr(href)}" alt="${escapeHtmlAttr(alt)}">`;
        }
    );
}

export function remoteImagesSessionKey(changeId: string): string {
    return `yoda:show-remote-images:${changeId}`;
}

export function readRemoteImagesSession(changeId?: string | null): boolean {
    if (!changeId || typeof sessionStorage === 'undefined')
        return false;
    try {
        return sessionStorage.getItem(remoteImagesSessionKey(changeId)) === '1';
    } catch {
        return false;
    }
}

export function writeRemoteImagesSession(changeId: string | null | undefined, enabled: boolean): void {
    if (!changeId || typeof sessionStorage === 'undefined')
        return;
    try {
        const key = remoteImagesSessionKey(changeId);
        if (enabled)
            sessionStorage.setItem(key, '1');
        else
            sessionStorage.removeItem(key);
    } catch {
        // Ignore quota / private-mode failures.
    }
}
